import { useMemo } from 'react';
import CvnaTier from '~/constants/vdp/cvnaTier';
import { useIsMobile } from '~/context/common/DeviceContext';
import type { CvnaCategory, CvnaFeature } from '~/contracts/vdp';
import {
  CardContainer,
  CardSVG,
  CardTitle,
  List,
  ListItem
} from './CategoryCard.styles';
import { categoryIconsMobile, categoryIconsDesktop } from './constants';
import Feature from './Feature';

function hasName(
  v: CvnaFeature
): v is CvnaFeature & Required<Pick<CvnaFeature, 'name'>> {
  return v.name != null;
}

function isStandoutFeature(v: CvnaFeature): boolean {
  return v.tier === CvnaTier.standout;
}

type OwnProps = {
  category: CvnaCategory;
  inModal?: 'inFeatureAndSpecModal';
};

export default function CategoryCard({ category, inModal }: OwnProps) {
  const { name: categoryName = '', cvnaFeatures = [] } = category;
  const isMobile = useIsMobile();

  const alphaSortedFeatures = useMemo(
    () =>
      [...cvnaFeatures]
        .filter(hasName)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [cvnaFeatures]
  );

  const iconToUse = isMobile ? categoryIconsMobile : categoryIconsDesktop;

  return (
    <CardContainer inModal={inModal}>
      <CardSVG
        alt={categoryName}
        src={`${process.env.CVNA_APP_CDN_ASSETS_PATH}${iconToUse[categoryName]}`}
      />
      <div>
        <CardTitle>{categoryName}</CardTitle>

        {inModal ? (
          <List className="list-disc">
            {alphaSortedFeatures.map(feature => {
              const { name, oemFeatureName } = feature;
              const displayName = oemFeatureName ?? name;

              return (
                <ListItem className="-:ml-12 -:pl-4" key={name}>
                  {displayName}
                </ListItem>
              );
            })}
          </List>
        ) : (
          <List>
            {alphaSortedFeatures.filter(isStandoutFeature).map(feature => {
              const { name, oemFeatureName } = feature;
              const displayName = oemFeatureName ?? name;

              return <Feature key={name} name={displayName} />;
            })}
          </List>
        )}
      </div>
    </CardContainer>
  );
}
